import { MouseEventHandler } from 'react';
import { StyleSheet } from 'aphrodite/no-important';
import { RATIO } from 'mm-theme-configuration/dist/consts';
import { Thumbnail } from '../image/image.types';
import { CroppingData, ImageHeightCalculationMethods, ImageWidths } from '../image/image.utils';

export interface Author {
    name: string;
    link?: string;
}

export interface MainCategoryData {
    displayName: string;
    link: string;
    isActive: boolean;
}

export interface CardComponentDataProps {
    id: string | number;
    title: string;
    articleUrl: string;
    image: Thumbnail;
    authors: Author[];
    mainCategory: MainCategoryData | null;
    featuredAt: string;
    coverType?: string;
    showDecorationLine?: boolean;
    numberOfLines?: number;
    lazyLoad?: boolean;
    description?: string;
    createdAt?: string;
    createdAtISO?: string;
    updatedAt?: string;
    updatedAtISO?: string;
    onClick?: MouseEventHandler;
    ampAnalyticsAction?: string;
    ampAnalyticsLabel?: string;
    ampAnalyticsCategory?: string;
    useFormatMinutesHoursAgo?: boolean;
}

export interface CardComponentSponsoredProps {
    sponsoredText?: string;
    brandName?: string;
    logo?: string;
}

export interface CardComponentPropsFromLayout {
    imageHeightCalculationMethods?: ImageHeightCalculationMethods;
    imageWidths: ImageWidths;
}

export const listAuthors = (authors: Array<Author>) => {
  return authors.map(author => author.name).join(', ');
};
export const isRatioIs4On5 = (aspectRatio: RATIO) => aspectRatio.x === 4 && aspectRatio.y === 5;

// The image-delivery-service leaks an editor crop's north-west offset into the
// following c_fill gravity, cropping the card to the top-left corner when the
// card's display ratio diverges from the editorial crop (e.g. a 4:5 vertical
// card built from a 16:9 editorial crop). When the crop diverges from every
// display ratio, drop it so g_auto smart-crops the original (single stage, so
// there is no offset to leak, and the fill has the full frame). Scoped to
// vertical cards, which are the portrait cards that trigger the leak.
const CROP_RATIO_DIVERGENCE_TOLERANCE = 0.1;

export const editorCropDivergesFromRatios = (cropping: CroppingData | null | undefined, aspectRatios: RATIO[]): boolean => {
  if (!cropping || !cropping.width || !cropping.height || aspectRatios.length === 0) {
    return false;
  }
  const cropRatio = cropping.width / cropping.height;
  return aspectRatios.every(aspectRatio => {
    if (!aspectRatio || aspectRatio.original === true || !aspectRatio.x || !aspectRatio.y) {
      return false;
    }
    const targetRatio = aspectRatio.x / aspectRatio.y;
    return Math.abs(cropRatio - targetRatio) > targetRatio * CROP_RATIO_DIVERGENCE_TOLERANCE;
  });
};
export const showMetadataInAnyScreenSize = (showAuthor: boolean, showDate: boolean) => showAuthor || showDate;

export const showAuthorInAnyScreenSize = (authors: Array<Author>, showAuthorInLargeScreen: boolean, showAuthorInMediumScreen: boolean, showAuthorInSmallScreen: boolean) => authors && authors.length > 0 && (showAuthorInLargeScreen || showAuthorInMediumScreen || showAuthorInSmallScreen);

export const showDateInAnyScreenSize = (showDateInLargeScreen: boolean, showDateInMediumScreen: boolean, showDateInSmallScreen: boolean) => showDateInLargeScreen || showDateInMediumScreen || showDateInSmallScreen;

export const showDescriptionInAnyScreenSize = (showDescriptionInLargeScreen: boolean, showDescriptionInMediumScreen: boolean, showDescriptionInSmallScreen: boolean) => showDescriptionInLargeScreen || showDescriptionInMediumScreen || showDescriptionInSmallScreen;

export const showPipeInAnyScreenSize = (showAuthor: boolean, showDate: boolean) => showAuthor && showDate;

export const showMetadataInLargeScreenSize = (showAuthorInLargeScreen: boolean, showDateInLargeScreen: boolean) => showAuthorInLargeScreen || showDateInLargeScreen;

export const showMetadataInMediumScreenSize = (showAuthorInMediumScreen: boolean, showDateInMediumScreen: boolean) => showAuthorInMediumScreen || showDateInMediumScreen;

export const showMetadataInSmallScreenSize = (showAuthorInSmallScreen: boolean, showDateInSmallScreen: boolean) => showAuthorInSmallScreen || showDateInSmallScreen;

export const shouldShowSponsored = (...params: (string | undefined)[]) => params.every(str => typeof str === 'string' && str.trim() !== '');

export const clickableLabelsStyles = () => StyleSheet.create({
  articleLink: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    zIndex: 100,
  },
  labelsLink: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    zIndex: 200,
  },
  categoryLink: {
    color: 'inherit',
  },
});
